import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader } from "../components/common";

export default function Referral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/referral"); setData(r.data); }
      catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return <FullLoader />;

  const link = `${window.location.origin}/signup?ref=${data.code}`;
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };
  const share = async () => {
    const text = `Join me on Jack Cash and we both earn ${data.reward_display}! Sign up with my link:`;
    try {
      if (navigator.share) await navigator.share({ title: "Join Jack Cash", text, url: link });
      else { await navigator.clipboard.writeText(`${text} ${link}`); toast.success("Invite copied"); }
    } catch {}
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Invite & Earn" subtitle={`Earn ${data.reward_display} per friend`} />

      <div className="rounded-3xl p-6 text-white shadow-[0_18px_44px_rgba(75,35,204,0.3)] mb-6"
        style={{ background: "linear-gradient(135deg,#6B3BF5,#4B23CC)" }}>
        <Gift size={28} className="mb-3" />
        <h2 className="font-display text-2xl font-light leading-tight">Give {data.reward_display}, get {data.reward_display}</h2>
        <p className="text-white/80 text-sm mt-2">Share your link. When a friend signs up and makes their first deposit, you both get {data.reward_display} in your wallet.</p>
      </div>

      <div className="rounded-3xl bg-card border border-border p-5 mb-6">
        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Your invite code</p>
        <div className="flex items-center justify-between gap-3">
          <span data-testid="referral-code" className="font-mono text-2xl font-bold tracking-wider text-primary">{data.code}</span>
          <button onClick={copy} data-testid="referral-copy" className="grid place-items-center w-11 h-11 rounded-full bg-secondary text-primary"><Copy size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button onClick={copy} data-testid="referral-copy-link" className="h-12 rounded-xl bg-secondary font-semibold flex items-center justify-center gap-2 text-sm"><Copy size={16} /> Copy link</button>
          <button onClick={share} data-testid="referral-share" className="h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 text-sm"><Share2 size={16} /> Share</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-card border border-border p-4 text-center">
          <Users size={18} className="mx-auto text-primary mb-1" />
          <div data-testid="referral-invited" className="text-2xl font-bold">{data.invited}</div>
          <div className="text-[11px] text-muted-foreground">Invited</div>
        </div>
        <div className="rounded-2xl bg-card border border-border p-4 text-center">
          <CheckCircle2 size={18} className="mx-auto text-success mb-1" />
          <div data-testid="referral-rewarded" className="text-2xl font-bold">{data.rewarded}</div>
          <div className="text-[11px] text-muted-foreground">Joined</div>
        </div>
        <div className="rounded-2xl bg-card border border-border p-4 text-center">
          <Gift size={18} className="mx-auto text-success mb-1" />
          <div data-testid="referral-earned" className="text-2xl font-bold amount-figure">{data.earned_display}</div>
          <div className="text-[11px] text-muted-foreground">Earned</div>
        </div>
      </div>
    </div>
  );
}
